export const DEFAULT_TARGET_COUNTRY_OPTIONS = [
  { value: "Denmark", label: "Denmark" },
  { value: "US", label: "US" },
  { value: "UK", label: "UK" },
  { value: "Canada", label: "Canada" },
  { value: "Australia", label: "Australia" },
  { value: "South Africa", label: "South Africa" },
  { value: "New Zealand", label: "New Zealand" },
];

const getTargetCountryUrl = () =>
  `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_TARGET_COUNTRY_API_PATH || "/target-country"}`;

const normalizeCountryLabel = (value) => `${value || ""}`.trim();

export const createCountryOption = (value) => {
  const label = normalizeCountryLabel(value);

  return label ? { value: label, label } : null;
};

export const mergeCountryOptions = (...optionLists) => {
  const merged = new Map();

  optionLists.flat().forEach((option) => {
    const normalizedOption =
      typeof option === "string" ? createCountryOption(option) : createCountryOption(option?.label || option?.value);

    if (normalizedOption) {
      merged.set(normalizedOption.label.toLowerCase(), normalizedOption);
    }
  });

  return Array.from(merged.values());
};

const extractCountryArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const preferredKeys = [
    "target_country",
    "target_countries",
    "target_country_list",
    "target_country_details",
    "countries",
    "country",
    "data",
  ];

  for (const key of preferredKeys) {
    if (Array.isArray(payload[key])) {
      return payload[key];
    }
  }

  for (const value of Object.values(payload)) {
    if (Array.isArray(value)) {
      return value;
    }
  }

  return [];
};

export const fetchTargetCountryOptions = async (jwtToken) => {
  try {
    const response = await fetch(getTargetCountryUrl(), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();
    const countryArray = extractCountryArray(data?.data || data);
    const apiOptions = mergeCountryOptions(countryArray);
    const missingDefaultCountries = collectNewTargetCountries(
      [DEFAULT_TARGET_COUNTRY_OPTIONS],
      apiOptions,
    );

    if (missingDefaultCountries.length) {
      const postResponse = await postNewTargetCountries(
        jwtToken,
        missingDefaultCountries,
      );

      if (!postResponse?.error) {
        return mergeCountryOptions(
          DEFAULT_TARGET_COUNTRY_OPTIONS,
          apiOptions,
          missingDefaultCountries,
        );
      }
    }

    return mergeCountryOptions(DEFAULT_TARGET_COUNTRY_OPTIONS, apiOptions);
  } catch {
    return [...DEFAULT_TARGET_COUNTRY_OPTIONS];
  }
};

export const normalizeCountrySelections = (value, options = []) => {
  if (!value) return [];

  let parsedValue = value;

  if (typeof value === "string") {
    try {
      parsedValue = JSON.parse(value);
    } catch {
      parsedValue = value.split(",").map((item) => item.trim());
    }
  }

  if (!Array.isArray(parsedValue)) {
    parsedValue = [parsedValue];
  }

  return mergeCountryOptions(
    options,
    parsedValue.map((item) =>
      typeof item === "object"
        ? item?.target_country || item?.country || item?.label || item?.value
        : item,
    ),
  ).filter((option) =>
    parsedValue.some((item) => {
      const label =
        typeof item === "object"
          ? item?.target_country || item?.country || item?.label || item?.value
          : item;

      return normalizeCountryLabel(label).toLowerCase() === option.label.toLowerCase();
    }),
  );
};

export const getCountryLabels = (selected = []) =>
  Array.isArray(selected)
    ? selected
        .map((item) => normalizeCountryLabel(item?.label || item?.value || item))
        .filter(Boolean)
    : [];

export const collectNewTargetCountries = (selectedLists = [], existingOptions = []) => {
  const existingCountrySet = new Set(
    mergeCountryOptions(existingOptions).map((option) => option.label.toLowerCase()),
  );

  const selectedCountries = selectedLists.flatMap((selected) => getCountryLabels(selected));

  return Array.from(
    new Set(
      selectedCountries.filter(
        (country) => !existingCountrySet.has(country.toLowerCase()),
      ),
    ),
  );
};

export const postNewTargetCountries = async (jwtToken, countries = []) => {
  const targetCountryList = Array.from(new Set(countries.filter(Boolean)));

  if (!targetCountryList.length) {
    return { skipped: true };
  }

  const response = await fetch(getTargetCountryUrl(), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${jwtToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      target_country: targetCountryList,
    }),
  });

  return response.json();
};
